<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ActivityCard from '../components/ActivityCard.vue'
import MapPanel from '../components/MapPanel.vue'
import StatePanel from '../components/StatePanel.vue'
import VenueCard from '../components/VenueCard.vue'
import DsIcon from '../components/DsIcon.vue'
import { api, listOf, queryString } from '../services/api'

type Tab = 'venues' | 'activities'
const route = useRoute()
const router = useRouter()
const tab = ref<Tab>(route.query.tab === 'activities' ? 'activities' : 'venues')
const view = ref<'list' | 'map'>('list')
const keyword = ref(String(route.query.q || ''))
const city = ref(String(route.query.city || ''))
const sort = ref('default')
const onlyAvailable = ref(false)
const items = ref<any[]>([])
const loading = ref(false)
const error = ref('')
const center = ref<{ lat: number; lng: number } | null>(null)
let requestVersion = 0

const totalText = computed(() => loading.value ? '正在搜索' : `找到 ${items.value.length} 个结果`)

function locate() {
  if (!navigator.geolocation) return
  navigator.geolocation.getCurrentPosition((position) => {
    center.value = { lat: position.coords.latitude, lng: position.coords.longitude }
    load()
  }, () => undefined, { timeout: 7000, maximumAge: 300000 })
}

async function load() {
  const current = ++requestVersion
  loading.value = true
  error.value = ''
  try {
    const coords: { lat?: number; lng?: number } = center.value || {}
    const path = tab.value === 'venues' ? '/venues/search' : '/activity/search'
    const params = tab.value === 'venues'
      ? { q: keyword.value, city: city.value, sort: sort.value, lat: coords.lat, lng: coords.lng, page: 1, pageSize: 50 }
      : { keyword: keyword.value, city: city.value, sort: sort.value, lat: coords.lat, lng: coords.lng, onlyAvailable: onlyAvailable.value || undefined, page: 1, pageSize: 50 }
    const result = await api<any>(`${path}${queryString(params)}`)
    if (current === requestVersion) items.value = listOf(result)
  } catch (reason: any) {
    if (current === requestVersion) error.value = reason?.message || '搜索失败'
  } finally {
    if (current === requestVersion) loading.value = false
  }
}

function setTab(next: Tab) {
  tab.value = next
  router.replace({ query: { ...route.query, tab: next } })
}

function selectMapItem(item: any) {
  const name = tab.value === 'venues' ? 'venue-detail' : 'activity-detail'
  router.push({ name, params: { id: item.id || item.activityId } })
}

watch([tab, sort, onlyAvailable], load)
onMounted(() => { load(); locate() })
</script>

<template>
  <section class="find-head">
    <div class="shell">
      <div class="find-title"><span class="section-kicker">FIND TENNIS</span><h1>今天想怎么打？</h1></div>
      <div class="search-bar">
        <DsIcon name="search" :size="20" /><input v-model="keyword" :placeholder="tab === 'venues' ? '搜索场馆名称或区域' : '搜索活动标题或场馆'" @keyup.enter="load" />
        <input v-model="city" class="city-input" placeholder="城市" @keyup.enter="load" />
        <button type="button" class="button button-primary" @click="load">搜索</button>
      </div>
      <div class="find-toolbar">
        <div class="segmented"><button :class="{ active: tab === 'venues' }" @click="setTab('venues')">找场馆</button><button :class="{ active: tab === 'activities' }" @click="setTab('activities')">找活动</button></div>
        <div class="toolbar-actions">
          <button class="icon-text-button" type="button" @click="locate"><DsIcon name="my_location" :size="18" />定位</button>
          <select v-model="sort" aria-label="排序"><option value="default">综合排序</option><option value="distance">距离最近</option><option v-if="tab === 'venues'" value="price">价格最低</option><option v-else value="time">时间最近</option></select>
          <label v-if="tab === 'activities'" class="check-chip"><input v-model="onlyAvailable" type="checkbox" />只看有空位</label>
          <button class="icon-text-button" type="button"><DsIcon name="tune" :size="18" />更多筛选</button>
          <div class="view-toggle"><button :class="{ active: view === 'list' }" title="列表" @click="view = 'list'"><DsIcon name="list" :size="18" /></button><button :class="{ active: view === 'map' }" title="地图" @click="view = 'map'"><DsIcon name="map" :size="18" /></button></div>
        </div>
      </div>
    </div>
  </section>

  <section class="shell find-results">
    <div class="results-meta"><b>{{ tab === 'venues' ? '场馆' : '活动' }}</b><span>{{ totalText }}</span></div>
    <StatePanel v-if="loading" mode="loading" />
    <StatePanel v-else-if="error" mode="error" :description="error" @retry="load" />
    <template v-else>
      <MapPanel v-if="view === 'map'" :items="items" :center="center" @select="selectMapItem" />
      <div v-else-if="items.length && tab === 'venues'" class="card-grid venue-grid"><VenueCard v-for="item in items" :key="item.id" :venue="item" /></div>
      <div v-else-if="items.length" class="activity-list"><ActivityCard v-for="item in items" :key="item.id" :activity="item" /></div>
      <StatePanel v-else mode="empty" :title="tab === 'venues' ? '没有匹配的场馆' : '附近还没有合适的球局'" description="换个关键词或筛选条件试试。" />
    </template>
  </section>
</template>
